
import { Component, ChangeDetectionStrategy, ViewEncapsulation, Input, OnChanges, SimpleChanges, OnDestroy, Output, EventEmitter, ChangeDetectorRef, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MasterDetailEditorBodyDirective, MasterDetailEditorComponent, MasterDetailEditorDisplayMode, MasterDetailEditorFooterDirective, MasterDetailEditorLeftActionsDirective, MasterDetailEditorRightActionsDirective } from '@workbuddy/ui/blocks/master.detail.editor';
import { AttachmentComponent } from '@workbuddy/ui/components/attachment';
import { ActivityEntityName, Attachment, DialogType, Entity, LabelType, MetadataAttachment, Shareable } from '@jms/model';
import { ActiveService, DialogService, SelectionService } from '@workbuddy/ui/services';
import { ButtonComponent } from '@workbuddy/ui/components/button';
import { Command } from '@workbuddy/ui/model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, of, tap } from 'rxjs';
import { LuxonCalendarModule } from '@workbuddy/ui/fields/luxon.calendar';
import { FieldButton, FieldButtonClickArgs, FieldModule } from '@jms/ui/ui.field';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { SharingProComponent } from '@workbuddy/ui/fields/sharing.pro';
import { SelectComponent } from '@workbuddy/ui/fields/select';
import { HttpClient } from '@angular/common/http';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { canUserChangeFile, FilesContext } from '../../model';
import { FilesService } from '../../sevices';
import { FilesDisplayMode } from '@workbuddy/ui/modules/files';
import { AttachmentEditorBaseComponent } from '@workbuddy/ui/components/attachment/editor/attachment.editor.base.component';
import { AlertComponent } from '@workbuddy/ui/components/alert';

@UntilDestroy()
@Component({
    selector: 'file-details-editor',
    templateUrl: 'file.details.editor.component.html',
    styleUrls: ['file.details.editor.component.less'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        AttachmentComponent,
        AlertComponent,
        ButtonComponent,
        LuxonCalendarModule,
        MasterDetailEditorComponent,
        MasterDetailEditorLeftActionsDirective,
        MasterDetailEditorRightActionsDirective,
        MasterDetailEditorFooterDirective,
        MasterDetailEditorBodyDirective,
        FieldModule,
        SharingProComponent,
        SelectComponent
    ],
    inputs: [
        'attachmentContext',
    ],
    outputs: [
        'attachmentUpdated',
    ]
})
export class FileDetailsEditorComponent extends AttachmentEditorBaseComponent implements OnChanges, OnDestroy {

    @Input() public context: FilesContext;
    @Input() public display = MasterDetailEditorDisplayMode.Default;

    private _attachment: MetadataAttachment;
    @Input() public set attachment(attachment: MetadataAttachment) {
        this._attachment = attachment;
        this.cdr.markForCheck();
    }

    public get attachment(): MetadataAttachment {
        return this._attachment;
    }

    @Output() public delete = new EventEmitter<Attachment>();

    protected get entity(): ActivityEntityName {
        return this?.context?.entity;
    }

    protected get timezone(): string {
        return this?.context?.timezone ?? 'utc';
    }

    protected get disableSubmit(): boolean {
        const invalid = (form: FormControl) => form.invalid;
        const isPristine = (form: FormControl) => form.pristine;
        const controls = [this.form, this.sharing, this.tags];

        return controls.some(invalid) || controls.every(isPristine);
    }

    protected toggleAddToDownloadsCommand = new Command({
        label: 'Add to download list',
        iconLeft: 'fa-plus',
        action: () => this.toggleAddToDownloads()
    });

    protected tags = new FormControl([]);
    public form = new FormControl();
    public sharing = new FormControl();

    protected buttons = [
        {
            label: 'Save',
            cssClass: 'btn-primary'
        },
        {
            label: 'Cancel',
            isCancel: true
        }
    ] as FieldButton[];

    protected editCommand: Command;

    protected $isEditableActivityFile = signal(false);

    protected http = inject(HttpClient);
    protected $tags = rxResource({
        params: () => ({ isEditableActivityFile: this.$isEditableActivityFile() }),
        stream: (request) => {
            const { isEditableActivityFile } = request.params;
            if (isEditableActivityFile) {
                return this.http.get<Entity[]>(`/api/label/tag/${LabelType.File}`);
            }
            return of([]);
        }
    })

    protected MasterDetailEditorDisplayMode = MasterDetailEditorDisplayMode;
    protected ActivityEntityName = ActivityEntityName;
    protected LabelType = LabelType

    constructor(
        private activeService: ActiveService<AttachmentComponent>,
        private selectionService: SelectionService<string>,
        protected dialogService: DialogService,
        protected filesService: FilesService,
        private cdr: ChangeDetectorRef,
    ) {
        super();
        this.selectionService
            .selection$
            .pipe(
                untilDestroyed(this),
                filter(_ => !!this.attachment),
                tap(_ => this.updateToggleAddToDownloadsCommand())
            )
            .subscribe();

        this.tags
            .valueChanges
            .pipe(
                takeUntilDestroyed(),
                filter(x => x !== this.attachment?.tags),
                tap(x => {
                    console.log('tags changed:', x);
                })
            )
            .subscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.attachment) {

            const { systemSharing, shared, sensitive, effectiveSharing } = this.attachment;

            const sharing = effectiveSharing ?? {
                systemSharing,
                shared,
                sensitive
            }

            this.updateToggleAddToDownloadsCommand();
            this.tags.setValue(this?.attachment?.tags ?? []);
            this.form.setValue(this.attachment);
            this.sharing.setValue(sharing);

            this.tags.markAsPristine();
            this.form.markAsPristine();
            this.sharing.markAsPristine();

            this.updateEditCommands();

        }

        this.$isEditableActivityFile.set(
            this.attachment?.entityName === ActivityEntityName.Activity &&
            canUserChangeFile(this.attachment) &&
            !this.context.readonly);
    }

    ngOnDestroy(): void { }

    protected toggleAddToDownloads(): void {

        //this.selectionService.toggle(this.activeService.activeItem);
        this.activeService.activeItem.toggleSelection()
        const isSelected = this.activeService.activeItem.selected;

        this.toggleAddToDownloadsCommand.iconLeft = isSelected
            ? 'fa-minus'
            : 'fa-plus';

        this.toggleAddToDownloadsCommand.label = !isSelected
            ? 'Add to download list'
            : 'Remove from download list';
    }

    protected updateEditCommands(): void {
        if (this.context.displayMode !== FilesDisplayMode.Picker && this.canEdit(this.attachment)) {
            this.editCommand = new Command({
                label: 'Edit',
                iconLeft: 'fa-edit',
                action: () => this.onEdit(),
            });
        } else {
            this.editCommand = null;
        }
    }

    protected get canMoveNext(): boolean {
        return this.activeService.keyManager.activeItemIndex < this.activeService.items.length - 1;
    }

    protected get canMovePrev(): boolean {
        return this.activeService.keyManager.activeItemIndex > 0;
    }

    protected get canExpand(): boolean {
        return true;
    }

    protected moveNext(): void {
        this.activeService.keyManager.setNextItemActive();
    }

    protected movePrev(): void {
        this.activeService.keyManager.setPreviousItemActive();
    }

    protected dismiss(): void {
        this.activeService.deactivate(this.activeService.activeItem);
    }

    protected onPreview(): void {
        this.dialogService.showModalDialog(DialogType.View);
    }

    protected onButtonClick(args: FieldButtonClickArgs): void {
        console.log(args);

        if ((args?.button as FieldButton)?.label === 'Save') {

            this.filesService.updateActivityLabels(this.attachment.entityId, this.tags.value)
            args.field.deactivate();
        }
    }

    private updateToggleAddToDownloadsCommand(): void {
        const isSelected = this.activeService.activeItem.selected;
        this.toggleAddToDownloadsCommand.iconLeft = isSelected
            ? 'fa-minus'
            : 'fa-plus';

        this.toggleAddToDownloadsCommand.label = !isSelected
            ? 'Add to download list'
            : 'Remove from download list';
    }

    protected onSharingChanged(info: Shareable) {
        this.filesService.updateActivitySharing(this.attachment.entityId, info)
    }

    protected onSave(): void {

        const tags = this.tags.value;
        const sharing = this.sharing.value;

        this.filesService.updateActivityLabels(this.attachment.entityId, tags);
        this.filesService.updateActivitySharing(this.attachment.entityId, {
            shared: sharing?.shared ?? [],
            sensitive: sharing?.sensitive,
            systemSharing: sharing?.systemSharing
        });

        this.dismiss();
    }

    private onEdit(): void {
        if (this.attachment.kind == 'Form' || this.attachment.type == 'Form') {
            this.onEditForm(this.attachment);
        }
    }

    protected onAttachmentUpdated(attachment: Attachment): void {
        super.onAttachmentUpdated(attachment);
        this.attachment = attachment;
        this.cdr.markForCheck();
    }
}